import { useState } from "react";
import { collection, getCountFromServer, getDocs, limit, orderBy, query, startAfter, Timestamp, where } from "firebase/firestore";
import getFirebase from "../../utilities/firebase";
import { createLogger } from "../../utilities/logger";

const log = createLogger("DataExportReportTool");

const COMPONENT_CLASS = "c-data-export-report-tool";

const REPORT_TYPE_SYMMIO_GROWTH = "symmio_growth";

const BATCH_SIZE = 1000;

const getValueShape = (value: any): string => {
    if (value == null || typeof value !== "object") {
        return typeof value;
    }
    return `object:{${Object.keys(value).join(",")}}`;
};

interface ReportEntity {
    label: string;
    collectionName: string;
}

const REPORT_ENTITIES: ReportEntity[] = [
    { label: "Users", collectionName: "users" },
    { label: "Organizations", collectionName: "organizations" },
    { label: "MSK Scores", collectionName: "mskScores" },
];

interface EntityYearCounts {
    label: string;
    yearCounts: { year: number; count: number }[];
}

const parseCreatedDate = (value: any): Date | null => {
    if (value == null) {
        return null;
    }

    if (value instanceof Timestamp) {
        return value.toDate();
    }

    if (value instanceof Date) {
        return value;
    }

    if (typeof value === "string") {
        const date = new Date(value);
        return isNaN(date.getTime()) ? null : date;
    }

    if (typeof value === "number") {
        const milliseconds = value > 1e12 ? value : value * 1000;
        const date = new Date(milliseconds);
        return isNaN(date.getTime()) ? null : date;
    }

    if (typeof value === "object" && value !== null) {
        const rawSeconds = value._seconds ?? value.seconds;
        const rawNanoseconds = value._nanoseconds ?? value.nanoseconds ?? 0;
        if (
            (typeof rawSeconds === "number" || typeof rawSeconds === "string") &&
            (typeof rawNanoseconds === "number" || typeof rawNanoseconds === "string")
        ) {
            const seconds = Number(rawSeconds);
            const nanoseconds = Number(rawNanoseconds);
            if (isFinite(seconds) && isFinite(nanoseconds)) {
                return new Timestamp(seconds, nanoseconds).toDate();
            }
        }
    }

    return null;
};

const fetchAllDocs = async (collectionName: string): Promise<any[]> => {
    const { db } = getFirebase();
    const docs: any[] = [];
    let lastDoc: any = null;

    while (true) {
        const q = lastDoc
            ? query(
                collection(db, collectionName),
                orderBy("created", "asc"),
                startAfter(lastDoc),
                limit(BATCH_SIZE)
            )
            : query(
                collection(db, collectionName),
                orderBy("created", "asc"),
                limit(BATCH_SIZE)
            );

        const snapshot = await getDocs(q);
        if (snapshot.docs.length === 0) {
            break;
        }

        snapshot.docs.forEach((doc) => docs.push(doc.data()));
        lastDoc = snapshot.docs[snapshot.docs.length - 1];

        if (snapshot.docs.length < BATCH_SIZE) {
            break;
        }
    }

    return docs;
};

const buildEntitySection = (entityCounts: EntityYearCounts): string[] => {
    const lines: string[] = [entityCounts.label];
    const years = entityCounts.yearCounts.map((yc) => yc.year);
    const counts = entityCounts.yearCounts.map((yc) => yc.count);

    lines.push(["", ...years.map(String)].join(","));
    lines.push(["Count", ...counts.map(String)].join(","));

    const totals = counts.map((count, index) =>
        counts.slice(0, index + 1).reduce((sum, c) => sum + c, 0)
    );
    lines.push(["Total", ...totals.map(String)].join(","));

    return lines;
};

const downloadCsv = (filename: string, content: string) => {
    const blob = new Blob(["\ufeff" + content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

const DataExportReportTool: React.FC = () => {
    const [reportType, setReportType] = useState(REPORT_TYPE_SYMMIO_GROWTH);
    const [isExporting, setIsExporting] = useState(false);
    const [error, setError] = useState("");
    const [status, setStatus] = useState("");
    const [results, setResults] = useState<EntityYearCounts[] | null>(null);
    const [diagnosticLines, setDiagnosticLines] = useState<string[]>([]);

    const executeReport = async () => {
        setIsExporting(true);
        setError("");
        setStatus("");
        setResults(null);
        setDiagnosticLines([]);

        try {
            const currentYear = new Date().getFullYear();
            const { db } = getFirebase();
            const diagnosticLinesLocal: string[] = [];

            const entityCounts: { entity: ReportEntity; yearCounts: Map<number, number> }[] = [];

            for (const entity of REPORT_ENTITIES) {
                setStatus(`Fetching ${entity.label}...`);
                const docs = await fetchAllDocs(entity.collectionName);

                const yearCounts = new Map<number, number>();
                const failureBuckets = new Map<string, { count: number; examples: any[] }>();

                docs.forEach((doc) => {
                    const date = parseCreatedDate(doc?.created);
                    if (date) {
                        const year = date.getFullYear();
                        yearCounts.set(year, (yearCounts.get(year) ?? 0) + 1);
                    } else {
                        const key = getValueShape(doc?.created);
                        const bucket = failureBuckets.get(key) ?? { count: 0, examples: [] };
                        bucket.count++;
                        if (bucket.examples.length < 5) {
                            bucket.examples.push(doc?.created);
                        }
                        failureBuckets.set(key, bucket);
                    }
                });

                const collectionRef = collection(db, entity.collectionName);
                const totalCount = (await getCountFromServer(collectionRef)).data().count;
                const missingCreatedCount = (
                    await getCountFromServer(
                        query(collectionRef, where("created", "==", null))
                    )
                ).data().count;

                const missingSamplesSnapshot = await getDocs(
                    query(collectionRef, where("created", "==", null), limit(10))
                );
                const missingSamples = missingSamplesSnapshot.docs.map((doc) => {
                    const data = doc.data();
                    return {
                        id: data?.id ?? doc.id,
                        created: data?.created,
                        updated: data?.updated,
                        lastLoggedIn: data?.lastLoggedIn,
                        email: data?.email,
                        organizationId: data?.organizationId,
                    };
                });

                let failureCount = 0;
                const bucketSummary: string[] = [];
                failureBuckets.forEach((bucket, key) => {
                    failureCount += bucket.count;
                    bucketSummary.push(
                        `    ${key} x${bucket.count} e.g. ${JSON.stringify(bucket.examples)}`
                    );
                });

                const summary = `[DataExportReport] ${entity.label}: total=${totalCount}, createdField=${totalCount - missingCreatedCount}, missingField=${missingCreatedCount}, fetched=${docs.length}, parsed=${docs.length - failureCount}, unparseable=${failureCount}`;
                bucketSummary.forEach((line) => log.info(line));
                log.info(
                    `[DataExportReport]   missing-created samples: ${JSON.stringify(missingSamples)}`
                );

                diagnosticLinesLocal.push(summary, ...bucketSummary,
                    `    missing-created samples: ${JSON.stringify(missingSamples)}`
                );

                entityCounts.push({ entity, yearCounts });
            }

            setDiagnosticLines(diagnosticLinesLocal);

            const entitiesWithData = entityCounts.filter((ec) => ec.yearCounts.size > 0);

            if (entitiesWithData.length === 0) {
                setError("No data found in any collection");
                return;
            }

            const startYear = Math.min(
                ...entitiesWithData.map((ec) => Math.min(...ec.yearCounts.keys()))
            );

            const years: number[] = [];
            for (let year = startYear; year <= currentYear; year++) {
                years.push(year);
            }

            const formatted: EntityYearCounts[] = entitiesWithData.map(({ entity, yearCounts }) => ({
                label: entity.label,
                yearCounts: years.map((year) => ({
                    year,
                    count: yearCounts.get(year) ?? 0,
                })),
            }));

            const csvLines: string[] = [];
            formatted.forEach((ec, index) => {
                if (index > 0) {
                    csvLines.push("");
                }
                csvLines.push(...buildEntitySection(ec));
            });

            downloadCsv("symmio-growth-report.csv", csvLines.join("\r\n"));
            setStatus("Report exported to symmio-growth-report.csv");
            setResults(formatted);
        } catch (e: any) {
            setError(e?.message ?? "Failed to generate the report");
        }

        setIsExporting(false);
    };

    return (
        <div className={COMPONENT_CLASS} style={{ maxWidth: 800 }}>
            <h3>Data Export Report</h3>
            <p style={{ marginBottom: 16 }}>
                Select a report type and execute it to export a CSV of year-over-year growth counts.
            </p>

            <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 8 }}>Report Type</label>
                <select
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                    style={{ padding: 8, minWidth: 200 }}
                >
                    <option value={REPORT_TYPE_SYMMIO_GROWTH}>Symmio Growth</option>
                </select>
            </div>

            <button
                onClick={executeReport}
                disabled={isExporting}
                style={{ padding: "10px 20px", cursor: isExporting ? "not-allowed" : "pointer" }}
            >
                {isExporting ? "Executing..." : "Execute Report"}
            </button>

            {status && <div style={{ marginTop: 16, color: "#666" }}>{status}</div>}
            {error && <div style={{ marginTop: 16, color: "#b91c1c" }}>{error}</div>}

            {diagnosticLines.length > 0 && (
                <div style={{ marginTop: 20 }}>
                    <h4>Diagnostics</h4>
                    <pre style={{
                        maxHeight: 300,
                        overflowY: "auto",
                        border: "1px solid #ccc",
                        padding: 10,
                        fontSize: 12,
                        fontFamily: "monospace",
                        whiteSpace: "pre-wrap",
                    }}>
                        {diagnosticLines.join("\n")}
                    </pre>
                </div>
            )}

            {results && (
                <div style={{ marginTop: 20 }}>
                    <h4>Report exported to symmio-growth-report.csv</h4>
                    {results.map((ec) => (
                        <div key={ec.label} style={{ marginBottom: 24 }}>
                            <h5>{ec.label}</h5>
                            <table style={{ borderCollapse: "collapse" }}>
                                <thead>
                                    <tr>
                                        <th style={thStyle}></th>
                                        {ec.yearCounts.map((yc) => (
                                            <th key={yc.year} style={thStyle}>{yc.year}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td style={tdStyle}>Count</td>
                                        {ec.yearCounts.map((yc) => (
                                            <td key={yc.year} style={tdStyle}>{yc.count}</td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td style={tdStyle}>Total</td>
                                        {ec.yearCounts.map((yc, index) => (
                                            <td key={yc.year} style={tdStyle}>
                                                {ec.yearCounts
                                                    .slice(0, index + 1)
                                                    .reduce((sum, item) => sum + item.count, 0)}
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const thStyle: React.CSSProperties = {
    border: "1px solid #d1d5db",
    padding: "6px 10px",
    textAlign: "left",
    fontSize: 13,
};
const tdStyle: React.CSSProperties = {
    border: "1px solid #d1d5db",
    padding: "6px 10px",
    fontSize: 13,
};

export default DataExportReportTool;
